// ── 회사별 대외비 비밀번호 정하기 칸 (2026-10-16, 서버 confidential-lock.route.js) ──────────
//   대외비 메뉴 = 급여 관리 · 연차정산 · 흐름(본사). 비밀번호는 회사마다 하나를 세 메뉴가 같이 쓴다.
//   두 곳에서 같이 쓴다:
//     운영자      — 테넌트 관리 › 회사 › (회사 펼침)  <ConfidentialLockPanel tenantCode={테넌트} onlyCode={회사} />
//     테넌트 관리자 — 업무/기능 배정 '회사 추가' 아래  <ConfidentialLockPanel />  (자기 테넌트 중 대외비 메뉴를 쓰는 회사, 없으면 칸 자체를 숨김)
//   비밀번호 원문은 서버로 보내기만 하고 어디에도 두지 않는다. 바꾸거나 없애면 그 회사 대외비 메뉴를 보던 사람은 바로 다시 잠긴다.
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { toKoreanDateTimeString } from '../../../utils/date';

const wrap = { border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 };
const input = { padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, background: 'var(--bg-card)', color: 'var(--text)', width: 170 };
const btn = { padding: '6px 12px', border: 'none', borderRadius: 7, background: 'var(--mm-tone-blue-solid-bg)', color: 'var(--mm-tone-blue-solid-text)', fontWeight: 700, fontSize: 12, cursor: 'pointer' };
const btnOutline = { ...btn, background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)' };
const btnDanger = { ...btnOutline, color: '#dc2626', borderColor: '#fca5a5' };
const muted = { fontSize: 12, color: 'var(--text-muted)' };

export default function ConfidentialLockPanel({ tenantCode = null, onlyCode = null }) {
  const { showToast } = useToast();
  const [rows, setRows] = useState(null);
  const [minLength, setMinLength] = useState(4);

  const load = useCallback(async () => {
    const q = tenantCode ? `?tenant=${encodeURIComponent(tenantCode)}` : '';
    const res = await apiClient.get(`/api/admin/confidential-lock/companies${q}`);
    if (!res.ok) { setRows([]); return; }
    setMinLength(Number(res.data?.min_length) || 4);
    const list = Array.isArray(res.data?.data) ? res.data.data : [];
    setRows(onlyCode ? list.filter((c) => c.code === String(onlyCode).toUpperCase()) : list);
  }, [tenantCode, onlyCode]);
  useEffect(() => { load(); }, [load]);

  if (!rows || rows.length === 0) return null;
  const inline = Boolean(onlyCode);

  const body = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((c) => (
          <LockRow key={c.code} company={c} minLength={minLength} showName={!inline} showToast={showToast} onChanged={load} />
        ))}
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        대외비 메뉴(급여 관리·연차정산, 본사는 흐름도)를 열 때 운영자·관리자·직원 모두 이 비밀번호를 넣어야 합니다. 한 번 풀면 함께 열리고, 다른 메뉴로 나가거나 10분 동안 쓰지 않으면 다시 잠깁니다.
      </span>
    </>
  );
  if (inline) {
    return (
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>대외비 비밀번호</div>
        {body}
      </div>
    );
  }
  return (
    <div style={wrap}>
      <strong style={{ fontSize: 14 }}>회사 대외비 비밀번호</strong>
      {body}
    </div>
  );
}

function LockRow({ company, minLength, showName, showToast, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);

  function close() { setEditing(false); setPw(''); setPw2(''); }

  async function save() {
    if (pw.length < minLength) { showToast(`대외비 비밀번호는 ${minLength}자 이상이어야 합니다.`, 'error'); return; }
    if (pw !== pw2) { showToast('두 번 넣은 비밀번호가 다릅니다.', 'error'); return; }
    setSaving(true);
    const res = await apiClient.put(`/api/admin/confidential-lock/companies/${encodeURIComponent(company.code)}`, { password: pw });
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '저장하지 못했습니다.', 'error'); return; }
    showToast(`${company.name} ${res.data?.message || '대외비 비밀번호를 저장했습니다.'}`, 'success');
    close();
    await onChanged();
  }

  async function clear() {
    if (!window.confirm(`${company.name} 대외비 비밀번호를 없앨까요?\n없애면 급여 관리·연차정산·흐름이 비밀번호 없이 바로 열립니다.`)) return;
    setSaving(true);
    const res = await apiClient.delete(`/api/admin/confidential-lock/companies/${encodeURIComponent(company.code)}`);
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '없애지 못했습니다.', 'error'); return; }
    showToast(`${company.name} ${res.data?.message || '대외비 비밀번호를 없앴습니다.'}`, 'success');
    await onChanged();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-confidential-lock-row={company.code}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {showName && <strong style={{ fontSize: 13, minWidth: 90 }}>{company.name}</strong>}
        <span style={{ fontSize: 12, fontWeight: 700, color: company.locked ? 'var(--mm-tone-blue-text, #2563eb)' : 'var(--text-muted)' }}>
          {company.locked ? '있음' : '없음'}
        </span>
        {company.locked && company.set_at && (
          <span style={muted}>{toKoreanDateTimeString(company.set_at)}{company.set_by_name ? ` · ${company.set_by_name}` : ''}</span>
        )}
        {!editing && (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button type="button" style={btn} disabled={saving} onClick={() => setEditing(true)}>{company.locked ? '바꾸기' : '정하기'}</button>
            {company.locked && <button type="button" style={btnDanger} disabled={saving} onClick={clear}>없애기</button>}
          </span>
        )}
      </div>
      {editing && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <input style={input} type="password" autoComplete="new-password" placeholder={`새 비밀번호 (${minLength}자 이상)`} aria-label="새 대외비 비밀번호"
            value={pw} onChange={(e) => setPw(e.target.value)} />
          <input style={input} type="password" autoComplete="new-password" placeholder="한 번 더" aria-label="새 대외비 비밀번호 확인"
            value={pw2} onChange={(e) => setPw2(e.target.value)} />
          <button type="button" style={btn} disabled={saving} onClick={save}>{saving ? '저장 중…' : '저장'}</button>
          <button type="button" style={btnOutline} disabled={saving} onClick={close}>취소</button>
        </div>
      )}
    </div>
  );
}
